// Algoritmo de sugestão de conciliação bancária.
// Puro TypeScript — sem dependências externas, totalmente testável.

export interface TxCandidate {
  id: string;
  direction: 'in' | 'out';
  amount_cents: number;
  description: string;
  occurred_at: string;
}

export interface PayableCandidate {
  id: string;
  payee: string;
  description: string;
  amount_cents: number;
  due: string;
  status: 'pending' | 'overdue';
}

export interface ReceivableCandidate {
  id: string;
  payee: string;
  description: string;
  amount_cents: number;
  due: string;
  status: 'pending' | 'overdue';
}

export interface MatchCandidate {
  target_id: string;
  target_kind: 'payable' | 'receivable';
  score: number;       // 0..1
  reasons: string[];   // ['valor exato', 'data ±2d', 'descrição similar']
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function daysDiff(a: string, b: string): number {
  return Math.abs((new Date(a).getTime() - new Date(b).getTime()) / 86400000);
}

// Levenshtein normalizado (0 = idêntico, 1 = completamente diferente)
function levenshteinSimilarity(a: string, b: string): number {
  const s1 = a.toLowerCase().trim();
  const s2 = b.toLowerCase().trim();
  if (!s1 || !s2) return 0;
  if (s1 === s2) return 1;

  const m = s1.length;
  const n = s2.length;
  const dp: number[][] = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  );

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i]![j] = s1[i - 1] === s2[j - 1]
        ? dp[i - 1]![j - 1]!
        : 1 + Math.min(dp[i - 1]![j]!, dp[i]![j - 1]!, dp[i - 1]![j - 1]!);
    }
  }
  return 1 - dp[m]![n]! / Math.max(m, n);
}

// ─── Score por candidato ───────────────────────────────────────────────────────

function scoreMatch(
  tx: TxCandidate,
  target: PayableCandidate | ReceivableCandidate,
  target_kind: 'payable' | 'receivable'
): MatchCandidate {
  let score = 0;
  const reasons: string[] = [];

  // Direção: 'out' → payable, 'in' → receivable
  if (tx.direction === 'out' && target_kind !== 'payable') return { target_id: target.id, target_kind, score: 0, reasons: [] };
  if (tx.direction === 'in' && target_kind !== 'receivable') return { target_id: target.id, target_kind, score: 0, reasons: [] };

  // Valor
  const diff = Math.abs(tx.amount_cents - target.amount_cents);
  if (diff === 0) {
    score += 0.5;
    reasons.push('valor exato');
  } else if (diff <= 100) { // ≤ R$ 1
    score += 0.3;
    reasons.push(`valor próximo (dif. ${(diff / 100).toFixed(2)})`);
  } else if (diff / target.amount_cents <= 0.05) { // ≤ 5%
    score += 0.15;
    reasons.push(`valor similar (${(diff / 100).toFixed(2)} de diferença)`);
  }

  // Data
  const days = daysDiff(tx.occurred_at, target.due);
  if (days <= 2) {
    score += 0.3;
    reasons.push(`data ±${days}d`);
  } else if (days <= 5) {
    score += 0.2;
    reasons.push(`data ±${days}d`);
  } else if (days <= 10) {
    score += 0.1;
    reasons.push(`data ±${days}d`);
  }

  // Descrição vs nome do favorecido
  const descSim = levenshteinSimilarity(tx.description, target.payee + ' ' + target.description);
  if (descSim >= 0.7) {
    score += 0.2;
    reasons.push(`descrição similar (${(descSim * 100).toFixed(0)}%)`);
  } else if (descSim >= 0.4) {
    score += 0.08;
    reasons.push(`descrição parcial (${(descSim * 100).toFixed(0)}%)`);
  }

  return { target_id: target.id, target_kind, score: Math.min(1, score), reasons };
}

// ─── Função principal ──────────────────────────────────────────────────────────

export function rankCandidates(
  tx: TxCandidate,
  payables: PayableCandidate[],
  receivables: ReceivableCandidate[]
): MatchCandidate[] {
  const candidates: MatchCandidate[] = [];

  if (tx.direction === 'out') {
    payables
      .filter((p) => p.status === 'pending' || p.status === 'overdue')
      .forEach((p) => {
        const m = scoreMatch(tx, p, 'payable');
        if (m.score >= 0.3) candidates.push(m);
      });
  } else {
    receivables
      .filter((r) => r.status === 'pending' || r.status === 'overdue')
      .forEach((r) => {
        const m = scoreMatch(tx, r, 'receivable');
        if (m.score >= 0.3) candidates.push(m);
      });
  }

  return candidates.sort((a, b) => b.score - a.score).slice(0, 5);
}
